import { useRef, useState } from 'react';

import {
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  Stack,
} from '@mui/material';
import {
  BotIcon,
  ChevronDownIcon,
  LanguagesIcon,
  SettingsIcon,
  SpellCheckIcon,
  WandSparklesIcon,
} from 'lucide-react';

import { PopperMenu } from '@modules/_shared/components/PopperMenu';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';
import { type Language } from '@shared/LocaleContext';

export const AI_LANGUAGE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'English', label: 'English' },
  { value: 'French', label: 'French' },
  { value: 'German', label: 'German' },
  { value: 'Spanish', label: 'Spanish' },
  { value: 'Italian', label: 'Italian' },
  { value: 'Portuguese', label: 'Portuguese' },
  { value: 'Polish', label: 'Polish' },
];

type AiDescriptionMenuProps = {
  onGenerate?: () => void;
  onImprove?: () => void;
  onExpertMode?: () => void;
  isLoading?: boolean;
  hasDescription?: boolean;
  translateFrom?: (sourceLanguage: Language) => Promise<void>;
  sourceLanguages?: { code: Language; name: string }[];
};

export const AiDescriptionMenu = ({
  onGenerate,
  onImprove,
  onExpertMode,
  isLoading,
  hasDescription,
  translateFrom,
  sourceLanguages,
}: AiDescriptionMenuProps) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [selectedTranslateLanguage, setSelectedTranslateLanguage] =
    useState<Language | null>(null);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <Stack
      direction="row"
      ref={anchorRef}
      sx={{
        alignSelf: 'center',
        mr: 2,
      }}
    >
      <Button
        variant="contained"
        loading={isLoading}
        loadingPosition="end"
        startIcon={<BotIcon />}
        endIcon={<ChevronDownIcon />}
        size="small"
        onClick={handleToggle}
      >
        {t('AI')}
      </Button>
      <PopperMenu
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
      >
        {onGenerate != null && (
          <MenuItem
            onClick={() => {
              onGenerate();
              setOpen(false);
            }}
          >
            <ListItemIcon>
              <WandSparklesIcon size={18} />
            </ListItemIcon>
            <ListItemText>{t('generate')}</ListItemText>
          </MenuItem>
        )}
        {hasDescription && onImprove != null && (
          <MenuItem
            onClick={() => {
              onImprove();
              setOpen(false);
            }}
          >
            <ListItemIcon>
              <SpellCheckIcon size={18} />
            </ListItemIcon>
            <ListItemText>{t('improve')}</ListItemText>
          </MenuItem>
        )}
        {sourceLanguages && sourceLanguages.length > 0 && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                setOpen(false);
                setSelectedTranslateLanguage(null);
                setTranslateDialogOpen(true);
              }}
            >
              <ListItemIcon>
                <LanguagesIcon size={18} />
              </ListItemIcon>
              <ListItemText>{t('Translate')}</ListItemText>
            </MenuItem>
          </>
        )}
        {onExpertMode != null && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                setOpen(false);
                onExpertMode();
              }}
            >
              <ListItemIcon>
                <SettingsIcon size={18} />
              </ListItemIcon>
              <ListItemText>{t('Expert mode')}</ListItemText>
            </MenuItem>
          </>
        )}
      </PopperMenu>
      <SlideDialog
        open={translateDialogOpen}
        onClose={() => setTranslateDialogOpen(false)}
        title={t('Select source language')}
        onSave={() => {
          if (selectedTranslateLanguage) {
            setTranslateDialogOpen(false);
            translateFrom?.(selectedTranslateLanguage);
          }
        }}
        saveText={t('Translate')}
        saveIcon={<LanguagesIcon />}
        saveDisabled={selectedTranslateLanguage == null}
      >
        <List>
          {sourceLanguages?.map(lang => (
            <ListItemButton
              key={lang.code}
              selected={selectedTranslateLanguage === lang.code}
              onClick={() => setSelectedTranslateLanguage(lang.code)}
            >
              <Radio
                checked={selectedTranslateLanguage === lang.code}
                size="small"
              />
              <ListItemText primary={lang.name} />
            </ListItemButton>
          ))}
        </List>
      </SlideDialog>
    </Stack>
  );
};
